import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from "react-leaflet";
import L from "leaflet";
import { MapPin, Building2, ArrowRight, Loader2, Building, Map, Cctv, Wifi, WifiOff, HeartPulse } from "lucide-react";
import ReactDOMServer from "react-dom/server";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import baseapi from "../services/baseapi";
import apBoundary from "../boundary/AndhraPradesh_Districts.json";
import stateBoundary from "../boundary/andhra_pradesh.json";
import { useMap } from "react-leaflet";


// Define a basic type for the GeoJSON structure to avoid {} errors
interface GeoJSONData {
    type: string;
    features: any[];
}


const typedApBoundary = apBoundary as unknown as GeoJSONData;


type ClientLocation = {
    coordinate: [number, number];
    client_id: number;
    client_name: string;
    region_name: string | null;
    state_name: string | null;
    district_name: string | null;
    town_name: string | null;
    is_offline: boolean;
};



interface DashboardData {
    overview: any;
    uptime: { stats: any; cameras: any[] };
    flapping: any[];
    reliability_dashboard?: { stats: any; cameras: any[] };
    manufacturer_health?: any[];
    health_timeseries: any[];
    trend_analysis?: { daily_events_30d: any[]; camera_downtime_trend: any[] };
    alerts: { stats: any; open_alerts: any[]; closed_30d: any[] };
    clients?: { total: number; total_online: number; total_offline: number };
}


const StatCard = ({ icon: Icon, label, value, color, bg, secondaryStats }: any) => (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 shadow-sm hover:border-[#422568]/20 transition-all group overflow-hidden relative">
        <div className={`p-2.5 rounded-xl ${bg} ${color} transition-transform group-hover:scale-110`}>
            <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-slate-800 uppercase tracking-widest leading-none mb-1.5 truncate">{label}</p>
            <div className="flex items-center justify-between">
                <p className="text-xl font-black text-slate-900 leading-none tracking-tight">{value}</p>
                <div className="flex items-center gap-1">
                    {secondaryStats?.map((stat: any, idx: number) => (
                        <div key={idx} className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md ${stat.bg || 'bg-[#422568]/10'} border ${stat.border || 'border-[#422568]/20'}`}>
                            <span className={`text-[9px] font-black ${stat.color || 'text-[#422568]'}`}>{stat.value}</span>
                            <span className={`text-[7px] font-black ${stat.labelColor || 'text-[#422568]/70'} uppercase tracking-tighter`}>{stat.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);


const getPinIcon = (isOffline: boolean) => {
    const colorClass = isOffline ? "text-red-500" : "text-green-500";
    const shadowColor = isOffline ? "rgba(239, 68, 68, 0.4)" : "rgba(34, 197, 94, 0.4)";


    return L.divIcon({
        html: ReactDOMServer.renderToString(
            <div className="flex items-center justify-center">
                <div className="relative flex items-center justify-center">
                    <MapPin
                        className={`h-8 w-8 ${colorClass} drop-shadow-[0_4px_8px_${shadowColor}]`}
                        fill="currentColor"
                        strokeWidth={1.8}
                    />
                    <div className="absolute top-[6px] flex items-center justify-center">
                        <Building className="h-3.5 w-3.5 text-white" strokeWidth={2.4} />
                    </div>
                </div>
            </div>
        ),
        className: "custom-client-marker",
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -28],
    });
};


export default function ClientSiteMap() {
    const navigate = useNavigate();


    // State for dynamic client data
    const [clientsData, setClientsData] = useState<ClientLocation[]>([]);
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [isLoading, setIsLoading] = useState(true);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const [clientsRes, statsRes] = await Promise.all([
                    baseapi.get('/clients', { params: { limit: 1000 } }),
                    baseapi.get('/dashboard/camera_unified_dashboard')
                ]);


                if (clientsRes.data?.status && clientsRes.data?.data) {
                    const mappedData = clientsRes.data.data
                        .filter((c: any) => c.latitude != null && c.longitude != null)
                        .map((c: any) => ({
                            coordinate: [c.latitude, c.longitude] as [number, number],
                            client_id: c.id,
                            client_name: c.name,
                            region_name: c.region_name,
                            state_name: c.state_name,
                            district_name: c.district_name,
                            town_name: c.town_name,
                            is_offline: !!c.is_offline
                        }));
                    setClientsData(mappedData);
                }


                if (statsRes.data?.status && statsRes.data?.data) {
                    setDashboardData(statsRes.data.data);
                }
            } catch (error) {
                console.error("Failed to fetch map/dashboard data:", error);
            } finally {
                setIsLoading(false);
            }
        };


        fetchData();
    }, []);


    const apCenter: [number, number] = [15.9129, 79.7400];


    // Use all features from the new AndhraPradesh_Districts.geojson file
    const apFeatures = useMemo(() => {
        return typedApBoundary.features;
    }, []);


    const apDistricts = useMemo(() => ({
        type: "FeatureCollection" as const,
        features: apFeatures
    }), [apFeatures]);


    // Pre-calculate unique labels at the center of each district to avoid duplicates
    const districtLabels = useMemo(() => {
        const labels: { name: string; position: [number, number] }[] = [];
        const seen = new Set<string>();


        apFeatures.forEach((feature: any) => {
            const name = feature.properties?.district_name;
            if (name && !seen.has(name)) {
                // Find a central point. For polygons, we use the average of the coordinates of the first ring
                let coords = [];
                if (feature.geometry.type === "Polygon") {
                    coords = feature.geometry.coordinates[0];
                } else if (feature.geometry.type === "MultiPolygon") {
                    // Pick the largest polygon (estimated by coordinate count) for the label
                    let largest = feature.geometry.coordinates[0][0];
                    feature.geometry.coordinates.forEach((p: any) => {
                        if (p[0].length > largest.length) largest = p[0];
                    });
                    coords = largest;
                }


                if (coords.length > 0) {
                    const lat = coords.reduce((sum: number, c: any) => sum + c[1], 0) / coords.length;
                    const lng = coords.reduce((sum: number, c: any) => sum + c[0], 0) / coords.length;
                    labels.push({ name, position: [lat, lng] });
                    seen.add(name);
                }
            }
        });
        return labels;
    }, [apFeatures]);


    // Create a world-covering mask with AP boundary as a hole
    const worldMaskGeoJSON = useMemo(() => {
        try {
            const apGeo = (stateBoundary as any)[0]?.geojson;
            if (!apGeo) return null;


            // World outer ring (covers multiple wraps to prevent edges showing on zoom out)
            const worldRing = [
                [-1000, -90], [1000, -90], [1000, 90], [-1000, 90], [-1000, -90]
            ];


            // Extract AP boundary coordinates as holes
            const holes: any[] = [];
            if (apGeo.type === 'Polygon') {
                holes.push(apGeo.coordinates[0]);
            } else if (apGeo.type === 'MultiPolygon') {
                apGeo.coordinates.forEach((poly: any[]) => {
                    holes.push(poly[0]);
                });
            }


            if (holes.length === 0) return null;


            return {
                type: "Feature" as const,
                properties: {},
                geometry: {
                    type: "Polygon" as const,
                    coordinates: [worldRing, ...holes]
                }
            };
        } catch (e) {
            console.error("Failed to create mask GeoJSON:", e);
            return null;
        }
    }, []);


    // Bounds to restrict panning to AP region
    const apBounds = L.latLngBounds(
        L.latLng(12.5, 76.5),  // Southwest corner
        L.latLng(19.5, 84.5)   // Northeast corner
    );
    const FitBounds = () => {
        const map = useMap();
        useEffect(() => {
            map.fitBounds(apBounds);
        }, [map]);
        return null;
    };




    return (
        <div className="w-full px-4 py-3 bg-slate-50 min-h-screen">
            <div className="mx-auto rounded-2xl">
                <div className="mb-3 flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2.5">
                        <div className="rounded-lg bg-[#422568]/10 p-1.5 text-[#422568] shadow-sm">
                            <Building2 className="h-4 w-4" />
                        </div>
                        <div>
                            <h2 className="text-sm font-extrabold text-slate-800">Geo Location</h2>
                            <p className="text-[11px] font-medium text-slate-500">Regional deployment sites</p>
                        </div>
                    </div>


                    <div className="rounded-md border border-[#422568]/20 bg-[#422568]/10 px-2.5 py-1 text-[10px] font-bold tracking-wider text-[#422568] shadow-sm uppercase">
                        {isLoading ? "Loading..." : `${clientsData.length} Site${clientsData.length !== 1 ? 's' : ''}`}
                    </div>
                </div>


                <div className="grid grid-cols-5 gap-3 mb-4">
                    <StatCard
                        icon={Cctv}
                        label="Total Cameras"
                        value={dashboardData?.overview?.total || 0}
                        color="text-slate-600"
                        bg="bg-slate-100"
                    />
                    <StatCard
                        icon={Wifi}
                        label="Online"
                        value={dashboardData?.overview?.reachable || 0}
                        color="text-emerald-600"
                        bg="bg-emerald-50"
                        secondaryStats={[
                            { value: dashboardData?.clients?.total_online || 0, label: "Sites" }
                        ]}
                    />
                    <StatCard
                        icon={WifiOff}
                        label="Offline"
                        value={dashboardData?.overview?.unreachable || 0}
                        color="text-rose-600"
                        bg="bg-rose-50"
                        secondaryStats={[
                            { value: dashboardData?.clients?.total_offline || 0, label: "Sites" }
                        ]}
                    />
                    <StatCard
                        icon={HeartPulse}
                        label="Health Score"
                        value={`${dashboardData?.overview?.health_percentage?.toFixed(1) || 0}%`}
                        color="text-blue-600"
                        bg="bg-blue-50"
                    />
                    <StatCard
                        icon={Building2}
                        label="Total Sites"
                        value={dashboardData?.clients?.total || 0}
                        color="text-indigo-600"
                        bg="bg-indigo-50"
                        secondaryStats={[
                            { value: dashboardData?.clients?.total_online || 0, label: "Online", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100", labelColor: "text-emerald-400" },
                            { value: dashboardData?.clients?.total_offline || 0, label: "Offline", color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-100", labelColor: "text-rose-400" }
                        ]}
                    />
                </div>


                <div className="flex justify-center">
                    <div className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2 shadow-inner">
                        <div className="h-[600px] z-40 overflow-hidden rounded-lg border border-slate-300 relative bg-white">


                            {/* Loading State Overlay */}
                            {isLoading ? (
                                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                                    <Loader2 className="h-8 w-8 animate-spin text-[#422568] mb-4" />
                                    <p className="text-sm font-bold text-slate-500 tracking-wide">Loading map data...</p>
                                </div>
                            ) : (
                                <MapContainer
                                    center={apCenter}
                                    zoom={7}
                                    scrollWheelZoom={true}
                                    style={{ width: "100%", height: "100%" }}
                                    className="z-40"
                                    maxBounds={apBounds}
                                    maxBoundsViscosity={1.0}
                                    minZoom={7}
                                    worldCopyJump={false}
                                    zoomAnimation={false}


                                >
                                    <FitBounds />
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        maxZoom={18}
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    />


                                    {/* White Mask - covers everything EXCEPT Andhra Pradesh */}
                                    {worldMaskGeoJSON && (
                                        <GeoJSON
                                            data={worldMaskGeoJSON as any}
                                            style={{
                                                color: "#ffffff",
                                                weight: 0,
                                                fillColor: "#ffffff",
                                                fillOpacity: 1,
                                            }}
                                        />
                                    )}
                                    <GeoJSON
                                        data={apDistricts as any}
                                        style={{
                                            color: "#475569",
                                            weight: 1,
                                            fillOpacity: 0
                                        }}
                                    />


                                    {/* Single, deduplicated Labels for districts */}
                                    {districtLabels.map((label, idx) => (
                                        <Marker
                                            key={`label-${idx}`}
                                            position={label.position}
                                            interactive={false}
                                            icon={L.divIcon({
                                                className: 'district-label-container',
                                                html: `<div class="district-label">${label.name}</div>`,
                                                iconSize: [120, 20],
                                                iconAnchor: [60, 10]
                                            })}
                                        />
                                    ))}


                                    <style>{`
                                        .district-label-container {
                                            background: transparent !important;
                                            border: none !important;
                                        }
                                        .district-label {
                                            color: #1e293b;
                                            font-size: 8px;
                                            font-weight: 900;
                                            text-transform: uppercase;
                                            letter-spacing: 0.02em;
                                            text-align: center;
                                            white-space: nowrap;
                                            text-shadow: 
                                                -1px -1px 0 #fff,  
                                                 1px -1px 0 #fff,
                                                -1px  1px 0 #fff,
                                                 1px  1px 0 #fff,
                                                 0px  2px 4px rgba(255,255,255,0.5);
                                        }
                                    `}</style>


                                    {/* AP Boundary Highlight */}
                                    <GeoJSON
                                        data={apDistricts as any}
                                        style={{
                                            color: "#422568",
                                            weight: 1.5,
                                            fillColor: "#422568",
                                        }}
                                    />


                                    {clientsData.map((item) => (
                                        <Marker
                                            key={item.client_id}
                                            position={item.coordinate}
                                            icon={getPinIcon(item.is_offline)}
                                        >
                                            <Popup className="custom-popup">
                                                <div className="min-w-[200px]">
                                                    <div className="mb-2">
                                                        <div className="text-sm font-extrabold text-slate-800">
                                                            {item.client_name}
                                                        </div>
                                                        <div className="mt-1.5 flex items-start gap-1 text-xs font-semibold text-slate-500 leading-tight">
                                                            <Map className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#422568]/70" />
                                                            <span>
                                                                {[item.town_name, item.district_name, item.state_name, item.region_name]
                                                                    .filter(Boolean)
                                                                    .join(', ') || 'Location details unavailable'}
                                                            </span>
                                                        </div>
                                                    </div>


                                                    <button
                                                        onClick={() =>
                                                            navigate(`/client-details?client_id=${item.client_id}&path=map`)
                                                        }
                                                        className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#422568] px-3 py-2 text-xs font-bold text-white transition-all shadow-md shadow-[#422568]/20 hover:bg-[#141425] hover:shadow-[#422568]/40"
                                                    >
                                                        View Details
                                                        <ArrowRight className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}


                                </MapContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}